import React, { forwardRef } from "react";

import ItemStatus from "pianofunclub-crm/components/General/ItemStatus";
import { getEnrolmentWarningText } from "pianofunclub-crm/utils/extractors";

import SelectRow from "pianofunclub-shared/components/ListItems/SelectRow";
import type { SelectRowRef } from "pianofunclub-shared/components/ListItems/SelectRow";
import type { LoadEnrolments_query_enrolments$data } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadEnrolments_query_enrolments.graphql";
import { NO_STAGE } from "pianofunclub-shared/utils/constants";
import {
  schoolYearConverter,
  combineWaitingListNotes,
  getEnrolmentStatus,
} from "pianofunclub-shared/utils/converters";
import { getFullName } from "pianofunclub-shared/utils/extractors";

export type EnrolmentItem = NonNullable<
  LoadEnrolments_query_enrolments$data["enrolments"]
>["edges"][number];

export interface EnrolmentSelectRowProps {
  data: NonNullable<NonNullable<EnrolmentItem>["node"]>;
  detailType: "TEACHER" | "PUPIL" | "TEACHER_AND_DAY" | undefined;
  initiallySelected: boolean;
  pressItemHandler: (
    data: NonNullable<NonNullable<EnrolmentItem>["node"]>,
    pupilFullName: string
  ) => void;
  selectItemHandler: (
    data: NonNullable<NonNullable<EnrolmentItem>["node"]>,
    pupilFullName: string
  ) => void;
}

const EnrolmentSelectRow = forwardRef<SelectRowRef, EnrolmentSelectRowProps>((props, ref) => {
  const { data, detailType, initiallySelected, pressItemHandler, selectItemHandler } = props;

  const status = getEnrolmentStatus({
    lastOfferedTimestamp: data.lastOfferedTimestamp,
    schoolArchived: data.school?.archived ?? false,
    status: data.status,
  });

  const pupilFullName = data.pupilFirstName
    ? getFullName(data.pupilFirstName ?? "", data.pupilLastName ?? "")
    : "";

  const notes = combineWaitingListNotes(data.pupilNotes, data.adminNotes);

  const warningText = getEnrolmentWarningText({
    hasInvalidParentPhoneNumber: data.hasInvalidParentPhoneNumber,
    potentialDuplicatePupils: data.potentialDuplicatePupils,
    potentialDuplicates: data.potentialDuplicates,
  });

  return (
    <SelectRow
      ref={ref}
      id={data.id}
      initiallySelected={initiallySelected}
      label={`${
        data.positionInWaitingList !== null ? `${data.positionInWaitingList} • ` : ""
      }${pupilFullName}${warningText ? " ⚠️" : ""} • ${data.instrument} • ${
        detailType === "PUPIL" && data.schoolYear !== null
          ? `${schoolYearConverter(data.schoolYear)}`
          : ""
      } • ${data.lessonStage !== null && data.lessonStage !== undefined && parseInt(data.lessonStage) !== NO_STAGE ? `Stage ${data.lessonStage}` : "Any Stage"}`}
      onPressItem={() => pressItemHandler(data, pupilFullName)}
      onSelect={() => selectItemHandler(data, pupilFullName)}
      renderRightElement={() => (
        <ItemStatus
          isTransparent
          label={status === "WAITING_LIST" ? "Waiting" : undefined}
          py={0}
          status={status}
          textProps={{
            fontFamily: "Poppins-Regular",
          }}
        />
      )}
      tooltipLabel={`${warningText ? `${warningText}` : ""}${warningText && notes ? "\n\n" : ""}${notes ? `${notes}` : ""}`}
    />
  );
});

export default React.memo(EnrolmentSelectRow);
